import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { User, Avaliation, WorkoutPlan } from "../../../types";
import { useAuth } from "../../../context/AuthContext";
import avaliations from "../../../services/avaliations";
import workoutplans from "../../../services/workoutplans";
import AvaliacaoForm from "../../avaliations/AvaliacaoForm";
import Objects from "../../../constants/Objects";
import Modal from "../../../tailadmin/components/ui/Modal";
import StudentWorkoutPlanForm from "../workout-plans/StudentWorkoutPlanForm";
import StudentProfileTab from "../tabs/StudentProfileTab";
import BodyAvaliationModal from "../../avaliations/BodyAvaliationModal";
import StudentWorkoutTrackingDashboard from "../dashboards/StudentWorkoutTrackingDashboard";
import StudentFoodLogDashboard from "../dashboards/StudentFoodLogDashboard";
import StudentActivityVsNutritionDashboard from "../dashboards/StudentActivityVsNutritionDashboard";
import StudentMonthlyPhotosTab from "../tabs/StudentMonthlyPhotosTab";
import {
  calculateIMC as calcIMC,
  calculatePercentualGordura,
  calculatePesoMagro,
  calculatePesoGordo,
  calculateDistribuicaoDobras,
  calculateSomatorioPerimetros,
} from "../../../helpers/avaliationCalculations";
import StudentManagementModalHeader from "./StudentManagementModalHeader";
import StudentManagementModalTabs, {
  type StudentManagementTab,
} from "./StudentManagementModalTabs";
import StudentManagementModalAvaliationTab from "./StudentManagementModalAvaliationTab";
import StudentManagementModalWorkoutTab from "./StudentManagementModalWorkoutTab";
import { ProfileTabSkeleton } from "./StudentManagementModalSkeletons";

interface StudentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: User | null;
}

interface AvaliationHistoryItem extends Avaliation {}

function getAvaliationTimestamp(avaliation: AvaliationHistoryItem): number {
  const value = avaliation.date || avaliation.registe || "";
  if (!value) return 0;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return time;
}

function getWorkoutTimestamp(plan: WorkoutPlan): number {
  const value = plan.created_at || plan.start_date || "";
  if (!value) return 0;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return time;
}

export default function StudentManagementModal({
  isOpen,
  onClose,
  student,
}: StudentManagementModalProps) {
  const { getUser } = useAuth();
  const [activeTab, setActiveTab] = useState<StudentManagementTab>("avaliation");
  const [avaliationList, setAvaliationList] = useState<AvaliationHistoryItem[]>(
    [],
  );
  const [workoutList, setWorkoutList] = useState<WorkoutPlan[]>([]);
  const [selectedAvaliation, setSelectedAvaliation] =
    useState<Avaliation | null>(null);
  const [selectedWorkout, setSelectedWorkout] = useState<WorkoutPlan | null>(
    null,
  );
  const [isAvaliationModalOpen, setIsAvaliationModalOpen] = useState(false);
  const [isWorkoutFormOpen, setIsWorkoutFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isBodyModalOpen, setIsBodyModalOpen] = useState(false);
  const [calculatedMetrics, setCalculatedMetrics] = useState({
    percentualGordura: null as number | null,
    imc: null as number | null,
    pesoMagro: null as number | null,
    pesoGordo: null as number | null,
    distribuicaoMMSS: null as number | null,
    distribuicaoMMII: null as number | null,
    distribuicaoTronco: null as number | null,
    somatorioPerimetros: null as number | null,
  });

  const handleOpenBodyModal = (av: Avaliation) => {
    if (!student) return;

    const fullAvaliation = {
      ...Objects.avalicao,
      ...av,
      user_id: student.id,
      professional_id: getUser()?.id,
    } as Avaliation;

    const gender =
      student.gener === "F" || student.gener === "Feminino" ? "F" : "M";

    const percentualGordura = calculatePercentualGordura(
      fullAvaliation,
      gender,
    );
    const imc = calcIMC(fullAvaliation);
    const pesoMagro = calculatePesoMagro(fullAvaliation, percentualGordura);
    const pesoGordo = calculatePesoGordo(fullAvaliation, pesoMagro);
    const dist = calculateDistribuicaoDobras(fullAvaliation);
    const somaPerimetros = calculateSomatorioPerimetros(fullAvaliation);

    setCalculatedMetrics({
      percentualGordura,
      imc,
      pesoMagro,
      pesoGordo,
      ...dist,
      somatorioPerimetros: somaPerimetros,
    });

    setSelectedAvaliation(fullAvaliation);
    setIsBodyModalOpen(true);
  };

  const loadData = useCallback(async () => {
    if (!student || !isOpen) return;
    const professional = getUser();
    if (!professional) return;

    setLoading(true);
    const avs = await avaliations.listall(
      String(professional.id),
      String(student.id),
    );
    const avHistory = (avs || []) as unknown as AvaliationHistoryItem[];
    const sortedAvs = [...avHistory].sort(
      (a, b) => getAvaliationTimestamp(b) - getAvaliationTimestamp(a),
    );
    setAvaliationList(sortedAvs);

    const plans = await workoutplans.listAll(
      String(professional.id),
      String(student.id),
    );
    const plansData = plans || [];
    const sortedPlans = [...plansData].sort(
      (a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a),
    );
    setWorkoutList(sortedPlans);
    setLoading(false);
  }, [student, isOpen, getUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = useCallback((tab: StudentManagementTab) => {
    setActiveTab(tab);
    setIsAvaliationModalOpen(false);
    setIsWorkoutFormOpen(false);
  }, []);

  // Previne scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  if (!isOpen || !student) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 sm:backdrop-blur-sm transition-all duration-300 p-0 ${
          isAvaliationModalOpen || isWorkoutFormOpen ? "hidden" : ""
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="relative flex flex-col overflow-hidden max-h-screen h-full w-screen rounded-none bg-white shadow-2xl dark:bg-boxdark transition-all duration-300">
          <StudentManagementModalHeader student={student} onClose={onClose} />

          <StudentManagementModalTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            avaliationCount={avaliationList.length}
            workoutCount={workoutList.length}
            isVisible={!!student}
          />

          <div className="flex-1 overflow-y-auto bg-gray-50/50 p-3 sm:p-4 md:p-6 dark:bg-gray-900/20 overscroll-contain">
            {activeTab === "avaliation" && (
              <StudentManagementModalAvaliationTab
                student={student}
                avaliationList={avaliationList}
                loading={loading}
                professionalId={String(getUser()?.id)}
                onOpenNewAvaliation={() => {
                  setSelectedAvaliation(null);
                  setIsAvaliationModalOpen(true);
                }}
                onOpenEditAvaliation={(av) => {
                  setSelectedAvaliation({
                    ...Objects.avalicao,
                    ...av,
                    user_id: student.id,
                    professional_id: getUser()?.id,
                  } as Avaliation);
                  setIsAvaliationModalOpen(true);
                }}
                onOpenBodyModal={handleOpenBodyModal}
                onDataReload={loadData}
              />
            )}

            {activeTab === "workout" && (
              <StudentManagementModalWorkoutTab
                workoutList={workoutList}
                loading={loading}
                onNewPlan={() => {
                  setSelectedWorkout(null);
                  setIsWorkoutFormOpen(true);
                }}
                onEditPlan={(plan) => {
                  setSelectedWorkout(plan);
                  setIsWorkoutFormOpen(true);
                }}
                onDeletePlan={async (plan) => {
                  await workoutplans.delete_plan(String(plan.id));
                  loadData();
                }}
              />
            )}

            {activeTab === "tracking" && (
              <StudentWorkoutTrackingDashboard
                studentId={String(student.id)}
                professionalId={String(getUser()?.id)}
              />
            )}

            {activeTab === "food" && (
              <StudentFoodLogDashboard studentId={String(student.id)} />
            )}

            {activeTab === "comparison" && (
              <StudentActivityVsNutritionDashboard
                studentId={String(student.id)}
                professionalId={String(getUser()?.id)}
              />
            )}

            {activeTab === "photos" && (
              <StudentMonthlyPhotosTab
                studentId={String(student.id)}
                professionalId={String(getUser()?.id)}
              />
            )}

            {activeTab === "profile" &&
              (loading ? (
                <ProfileTabSkeleton />
              ) : (
                <StudentProfileTab
                  student={student}
                  professionalId={String(getUser()?.id)}
                  onClose={onClose}
                />
              ))}
          </div>
        </div>
      </div>

      {isAvaliationModalOpen && (
        <AvaliacaoForm
          isOpen={isAvaliationModalOpen}
          onBack={() => {
            setIsAvaliationModalOpen(false);
            loadData();
          }}
          data={
            {
              ...selectedAvaliation,
              user_id: student.id,
              professional_id: getUser()?.id,
            } as Avaliation
          }
        />
      )}

      {isBodyModalOpen && selectedAvaliation && (
        <BodyAvaliationModal
          isOpen={isBodyModalOpen}
          onClose={() => setIsBodyModalOpen(false)}
          studentName={student.name}
          professionalName={getUser()?.name || "Profissional"}
          data={selectedAvaliation}
          calculated={calculatedMetrics}
          date={selectedAvaliation.registe || selectedAvaliation.date}
        />
      )}

      {isWorkoutFormOpen && (
        <Modal
          isOpen={isWorkoutFormOpen}
          onClose={() => setIsWorkoutFormOpen(false)}
          isExpanded
          className="!p-0 [&>button]:hidden"
        >
          <StudentWorkoutPlanForm
            initialData={selectedWorkout}
            studentId={String(student.id)}
            professionalId={String(getUser()?.id)}
            onClose={() => setIsWorkoutFormOpen(false)}
            onSuccess={() => {
              setIsWorkoutFormOpen(false);
              loadData();
            }}
          />
        </Modal>
      )}
    </>,
    document.body,
  );
}
